import React, { createContext, useContext, useState, useCallback, ReactNode, useEffect } from 'react';

// Sync modes for different use cases
export type SyncMode = 'none' | 'local' | 'external';

export interface NavigationSyncState {
  expandedGroups: Record<string, boolean>;
  activeSection: string | null;
  scrollPositions: Map<string, number>;
  userInitiated: boolean;
}

export interface NavigationSyncContextType {
  state: NavigationSyncState;
  toggleGroup: (groupName: string, userInitiated?: boolean) => void;
  setGroupExpanded: (groupName: string, expanded: boolean, userInitiated?: boolean) => void;
  setActiveSection: (section: string | null) => void;
  saveScrollPosition: (section: string, position: number) => void;
  getScrollPosition: (section: string) => number | undefined;
  expandGroupForItem: (itemId: string, groupId: string) => void;
}

const NavigationSyncContext = createContext<NavigationSyncContextType | undefined>(undefined);

interface NavigationSyncProviderProps {
  children: ReactNode;
  initialExpandedGroups?: Record<string, boolean>;
  onStateChange?: (state: NavigationSyncState) => void;
}

export function NavigationSyncProvider({ 
  children, 
  initialExpandedGroups = {},
  onStateChange
}: NavigationSyncProviderProps) {
  const [state, setState] = useState<NavigationSyncState>({
    expandedGroups: initialExpandedGroups,
    activeSection: null,
    scrollPositions: new Map(),
    userInitiated: false
  });

  // Notify parent of state changes
  useEffect(() => {
    onStateChange?.(state);
  }, [state, onStateChange]);

  const toggleGroup = useCallback((groupName: string, userInitiated = true) => {
    setState(prev => ({
      ...prev,
      expandedGroups: {
        ...prev.expandedGroups,
        [groupName]: !prev.expandedGroups[groupName]
      },
      userInitiated
    }));
  }, []);

  const setGroupExpanded = useCallback((groupName: string, expanded: boolean, userInitiated = true) => {
    setState(prev => ({
      ...prev,
      expandedGroups: {
        ...prev.expandedGroups,
        [groupName]: expanded
      },
      userInitiated
    }));
  }, []);

  const setActiveSection = useCallback((section: string | null) => {
    setState(prev => ({
      ...prev,
      activeSection: section,
      userInitiated: false
    }));
  }, []);

  const saveScrollPosition = useCallback((section: string, position: number) => {
    setState(prev => {
      const newScrollPositions = new Map(prev.scrollPositions);
      newScrollPositions.set(section, position);
      return {
        ...prev,
        scrollPositions: newScrollPositions
      };
    });
  }, []);

  const getScrollPosition = useCallback((section: string): number | undefined => {
    return state.scrollPositions.get(section);
  }, [state.scrollPositions]);

  // Special method for search result selection - expands group and highlights item
  const expandGroupForItem = useCallback((itemId: string, groupId: string) => {
    setState(prev => ({
      ...prev,
      expandedGroups: {
        ...prev.expandedGroups,
        [groupId]: true
      },
      activeSection: itemId,
      userInitiated: true
    }));
  }, []);

  return (
    <NavigationSyncContext.Provider value={{
      state,
      toggleGroup,
      setGroupExpanded,
      setActiveSection,
      saveScrollPosition,
      getScrollPosition,
      expandGroupForItem
    }}>
      {children}
    </NavigationSyncContext.Provider>
  );
}

// Hook with optional context (returns local state if no provider)
export function useNavigationSync(fallbackToLocal = true): NavigationSyncContextType | null {
  const context = useContext(NavigationSyncContext);
  
  // If no context and fallback enabled, return null (TreeNav will use local state)
  if (context === undefined && fallbackToLocal) {
    return null;
  }
  
  // If no context and no fallback, throw error
  if (context === undefined && !fallbackToLocal) {
    throw new Error('useNavigationSync must be used within a NavigationSyncProvider');
  }
  
  return context;
}

// Compatibility hook for existing code
export function useGroupExpanded() {
  const syncContext = useNavigationSync(true);
  
  // If we have a sync context, use it
  if (syncContext) {
    return {
      expandedGroups: syncContext.state.expandedGroups,
      toggleGroup: syncContext.toggleGroup,
      setGroupExpanded: syncContext.setGroupExpanded
    };
  }
  
  // Return a mock that TreeNav will override with local state
  return null;
}
